import type { ReactNode } from 'react';
import VerificationRule from './VerificationRule';
import { VERIFICATION_LABELS, formatQuantity, type LegendaLang, type Verification } from './vocabulary';

export interface ConflictSide {
  /** Where in the document this figure is printed, e.g. `Введение · с. 6`. */
  location: string;
  value: number;
  unit: string;
  verification: Verification;
  evidenceRegionId?: string;
}

export interface ConflictPairProps {
  title: string;
  lang: LegendaLang;
  a: ConflictSide;
  b: ConflictSide;
  fractionDigits?: number;
  /** Year, review state and severity chips. */
  status?: ReactNode;
  footer?: ReactNode;
}

const NOT_AVERAGED: Record<LegendaLang, string> = {
  ru: 'Значения не усредняются. Обе величины сохранены с атрибуцией; выбор — решение рецензента.',
  hy: 'Արժեքները չեն միջինացվում. Երկուսն էլ պահվում են վերագրումով; ընտրությունը գրախոսի որոշումն է.',
  en: 'Values are not averaged. Both figures survive, attributed; choosing one is a reviewer decision.',
};

const COMPUTED: Record<LegendaLang, string> = {
  ru: 'расчёт кода · |a − b|',
  hy: 'կոդի հաշվարկ · |a − b|',
  en: 'computed by code · |a − b|',
};

/**
 * Two figures the archive prints for the same quantity.
 *
 * The doubled rule and the `≠` brace are structural, not decorative: there is
 * no layout in which one of these values can quietly become "the answer". The
 * difference is stated as a derived quantity in the deterministic voice, and
 * the component has no prop that would let a caller collapse the pair.
 *
 * This is the most valuable thing on the screen when it appears — an
 * unreconciled contradiction the archive has carried since 1982 — so it is
 * given the weight of a finding rather than the styling of a warning.
 */
export default function ConflictPair({
  title,
  lang,
  a,
  b,
  fractionDigits = 2,
  status,
  footer,
}: ConflictPairProps) {
  const delta = Math.abs(a.value - b.value);

  const renderSide = (side: ConflictSide) => (
    <VerificationRule status={side.verification} lang={lang} labelInLegend className="lg-cpair__side">
      <div className="lg-ev__param">{side.location}</div>
      <div className="lg-ev__value">
        {formatQuantity(side.value, lang, fractionDigits)}
        <span className="lg-ev__unit"> {side.unit}</span>
      </div>
      <div className="lg-ev__meta">
        <span className="lg-chip">{VERIFICATION_LABELS[side.verification][lang]}</span>
        {side.evidenceRegionId ? <span className="lg-chip">{side.evidenceRegionId}</span> : null}
      </div>
    </VerificationRule>
  );

  return (
    <section className="lg-cpair" data-conflict="unresolved">
      <header className="lg-cpair__head">
        <span>{title}</span>
        {status}
      </header>

      <div className="lg-cpair__body">
        {renderSide(a)}
        <div className="lg-cpair__brace" aria-hidden="true">
          ≠
        </div>
        {renderSide(b)}
      </div>

      <footer className="lg-cpair__delta">
        <span className="lg-cpair__delta-value">
          Δ {formatQuantity(delta, lang, fractionDigits)} {a.unit}
        </span>
        <span className="lg-chip lg-chip--derived">{COMPUTED[lang]}</span>
        <span style={{ color: 'var(--lg-ink-2)' }}>{NOT_AVERAGED[lang]}</span>
        {footer}
      </footer>
    </section>
  );
}
